import { Component, Inject, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { formatIdleWindowLabel } from 'src/app/core/utils/idle-window-label';

/** What the caller must hand over so the dialog can render the live countdown. */
export interface IdleWarningDialogData {
  /** Seconds left before the forced sign-out, updated by IdleTimeoutService. */
  remainingSeconds: Signal<number>;
  /** Length of the inactivity window, in minutes — shown in the message. */
  idleTimeoutMinutes: number;
  /** Full countdown length, used for the progress bar. */
  warningSeconds: number;
}

/** Result returned to IdleTimeoutService when the user answers the warning. */
export type IdleWarningDialogResult = 'stay' | 'logout';

@Component({
  selector: 'app-idle-warning-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
  ],
  template: `
    <div class="idle-header">
      <mat-icon class="idle-icon">timer_off</mat-icon>
      <h5 mat-dialog-title class="idle-title">Your session is about to expire</h5>
    </div>

    <div mat-dialog-content class="idle-content">
      <p class="idle-message">
        You have been inactive for {{ inactivityLabel() }}.
        For security reasons you will be signed out automatically.
      </p>
      <p class="idle-countdown">
        Signing out in <strong>{{ data.remainingSeconds() }}</strong>
        {{ data.remainingSeconds() === 1 ? 'second' : 'seconds' }}
      </p>
      <mat-progress-bar
        mode="determinate"
        color="warn"
        [value]="progress()">
      </mat-progress-bar>
    </div>

    <div mat-dialog-actions class="idle-actions">
      <button
        mat-stroked-button
        class="idle-btn"
        (click)="dialogRef.close('logout')">
        Sign out now
      </button>
      <button
        mat-flat-button
        color="primary"
        class="idle-btn"
        cdkFocusInitial
        (click)="dialogRef.close('stay')">
        Stay signed in
      </button>
    </div>
  `,
  styles: [`
    .idle-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 22px 24px 0;
    }
    .idle-icon {
      color: #f4433699;
      font-size: 28px;
      width: 28px;
      height: 28px;
    }
    .idle-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
    .idle-content {
      padding: 12px 24px 4px;
      min-width: 320px;
    }
    .idle-message {
      margin: 0 0 12px;
      font-size: 14px;
      line-height: 1.5;
    }
    .idle-countdown {
      margin: 0 0 12px;
      font-size: 15px;
    }
    .idle-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 12px 24px 20px;
    }
    .idle-btn {
      min-width: 140px;
    }
  `],
})
export class IdleWarningDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<IdleWarningDialogComponent, IdleWarningDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: IdleWarningDialogData
  ) {}

  /** Inactivity window written the way a human would say it ("2 hours"). */
  inactivityLabel(): string {
    return formatIdleWindowLabel(this.data.idleTimeoutMinutes);
  }

  /** Countdown rendered as a shrinking bar. */
  progress(): number {
    if (!this.data.warningSeconds) return 0;
    return (this.data.remainingSeconds() / this.data.warningSeconds) * 100;
  }
}
